// Shared dialog/drawer component used across the Planner (and
// reusable elsewhere). Replaces the prior ad-hoc pattern where
// each modal shipped its own backdrop, focus-trap, Escape
// handler, and ad-libbed transitions. Two variants:
//
//   * variant="center" -- classic centered dialog with a scale-in
//     entrance animation.
//   * variant="right"  -- right-side slide-in drawer (used by
//     long-form editors like ModuleDrawer + SnippetLibrary).
//
// Both share: backdrop click + Escape close, focus trap inside,
// body scroll lock, header with title + close button, optional
// Sheet.Body (scrollable) + Sheet.Footer (action bar) subparts.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import useFocusTrap from "../../a11y/useFocusTrap";

const TRANSITION_MS = 220;

const CENTER_WIDTHS = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

const RIGHT_WIDTHS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-xl",
  xl: "max-w-2xl",
};

// Bottom variant is full-width by design (mobile bottom sheets).
// The size knob caps max-height so a "lg" sheet can take more
// vertical real estate; "md" keeps comfortable thumb reach.
const BOTTOM_HEIGHTS = {
  sm: "max-h-[60vh]",
  md: "max-h-[80vh]",
  lg: "max-h-[90vh]",
  xl: "max-h-[95vh]",
};

export default function Sheet({
  open,
  onClose,
  title,
  variant = "center",
  size = "md",
  children,
  // Optional aria override -- when the parent renders its own
  // header (and so title=undefined), it can still wire labelling
  // by passing the element's id here.
  ariaLabelledBy,
  // Hide the default header (close button + title). The parent
  // takes responsibility for rendering its own close affordance.
  hideHeader = false,
  // Extra classes for the inner dialog element.
  className = "",
}) {
  // Internal phase state drives the enter/exit transitions.
  //   closed  -> nothing in the DOM
  //   opening -> mounted with initial (off-screen / scaled-down)
  //              classes; next frame flips to ``open``.
  //   open    -> fully visible, transition complete.
  //   closing -> classes back to off-screen; we stay mounted for
  //              TRANSITION_MS so the exit animation can play
  //              out, then drop to ``closed``.
  const [phase, setPhase] = useState("closed");

  useEffect(() => {
    if (open && (phase === "closed" || phase === "closing")) {
      setPhase("opening");
      // Defer the flip so the browser paints the initial state
      // once -- without this rAF the transition starts from the
      // final position and visually skips.
      const id = requestAnimationFrame(() => setPhase("open"));
      return () => cancelAnimationFrame(id);
    }
    if (!open && (phase === "open" || phase === "opening")) {
      setPhase("closing");
      const id = setTimeout(() => setPhase("closed"), TRANSITION_MS);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open, phase]);

  // Body-scroll lock + Escape close are keyed on the ``open`` prop
  // (not the internal phase) so the previous overflow is captured
  // exactly once when the parent opens the sheet, and restored
  // exactly once when the parent closes it. Tying these to
  // ``phase`` instead re-runs the effect on each transition step
  // and clobbers the captured original.
  //
  // ``onClose`` must NOT be in the dep array even though the
  // listener uses it -- most parents pass an inline arrow which
  // changes identity on every render, and that turned this
  // "once-on-open" effect into a "every-parent-render" effect.
  // Symptom: after a long import (many parent re-renders), the
  // teardown captured ``document.body.style.overflow = "hidden"``
  // as the supposed "previous" value because a sibling re-run
  // hadn't restored it yet, and the modal close then "restored"
  // body.style to "hidden" permanently -- writer had to refresh
  // to scroll the planner again. Ref the latest onClose so the
  // listener always calls the freshest one.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const trapRef = useFocusTrap(phase === "open" || phase === "opening");

  if (phase === "closed" || typeof document === "undefined") return null;

  const visible = phase === "open";
  const isCenter = variant === "center";
  const isBottom = variant === "bottom";
  const isRight = !isCenter && !isBottom;

  const widthClass = isCenter
    ? CENTER_WIDTHS[size] ?? CENTER_WIDTHS.md
    : isRight
      ? RIGHT_WIDTHS[size] ?? RIGHT_WIDTHS.md
      : ""; // bottom: full-width

  // Container layout differs between variants:
  //   center -- flex into the middle of the viewport
  //   right  -- pin to the right edge, full viewport height
  //   bottom -- pin to the bottom edge, full viewport width
  //             (the canonical mobile bottom-sheet pattern)
  //
  // ``pointer-events`` is gated on the visible phase. If the
  // sheet is mid-transition (``opening`` / ``closing``) — or ever
  // gets wedged in a non-``open`` phase by a quick open/close
  // race — the fixed-inset overlay must not intercept clicks
  // intended for the page below. Without this guard, a stuck
  // ``opening`` phase produces the canonical "scroll works but
  // nothing else clicks" symptom: the invisible overlay (opacity
  // 0) is still topmost and swallows every pointer event, while
  // wheel scroll delegates up to the body and keeps working.
  const containerBaseClass = isCenter
    ? "fixed inset-0 z-[80] flex items-center justify-center p-4"
    : isBottom
      ? "fixed inset-0 z-[80] flex items-end justify-stretch"
      : "fixed inset-0 z-[80] flex items-stretch justify-end";
  const containerClass = `${containerBaseClass} ${
    visible ? "pointer-events-auto" : "pointer-events-none"
  }`;

  const dialogTransitionClass = isCenter
    ? `transition-all duration-200 ease-out ${
        visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`
    : isBottom
      ? `transition-transform duration-200 ease-out ${
          visible ? "translate-y-0" : "translate-y-full"
        }`
      : `transition-transform duration-200 ease-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`;

  const bottomHeightClass = BOTTOM_HEIGHTS[size] ?? BOTTOM_HEIGHTS.md;

  const dialogBaseClass = isCenter
    ? `relative w-full ${widthClass} rounded-2xl border border-border bg-surface shadow-xl max-h-[90vh] overflow-hidden flex flex-col focus:outline-none`
    : isBottom
      ? `relative w-full ${bottomHeightClass} rounded-t-2xl border-t border-border bg-surface shadow-xl overflow-hidden flex flex-col focus:outline-none`
      : `relative w-full ${widthClass} h-full border-l border-border bg-surface shadow-xl overflow-hidden flex flex-col focus:outline-none`;

  const labelledBy = ariaLabelledBy || (title ? "sheet-title" : undefined);

  return createPortal(
    <div
      className={containerClass}
      onClick={(e) => {
        // Only close when the click hits the backdrop itself --
        // bubbled clicks from inside the dialog must not dismiss.
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      {/* Backdrop. Sibling element so its opacity can animate
          independently of the dialog's transform. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ease-out ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`${dialogBaseClass} ${dialogTransitionClass} ${className}`}
      >
        {!hideHeader && (title || onClose) ? (
          // Header chrome unified with the Browse book-preview
          // modal (src/components/UserHome/Modal.jsx). Three things
          // changed in the refinement pass:
          //   * Title hierarchy bumped from text-sm/base to
          //     text-base/lg — small on desktop felt cramped
          //     against the close pill; the larger size matches
          //     how the Browse modal's hero title reads.
          //   * Close button gained a focus-visible ring so
          //     keyboard users get a clear target indicator;
          //     previously the only feedback was the hover bg.
          //   * Padding scales to the same px-4 sm:px-5 / py-3
          //     sm:py-3.5 the body + footer use, so the three
          //     regions stack with consistent horizontal alignment
          //     and vertical rhythm at every breakpoint.
          <div className="flex items-center gap-2 border-b border-border/60 px-4 sm:px-5 py-3 sm:py-3.5 shrink-0">
            {title ? (
              <h2
                id="sheet-title"
                className="text-base sm:text-lg font-semibold text-text min-w-0 truncate"
              >
                {title}
              </h2>
            ) : (
              <span aria-hidden="true" className="min-w-0 flex-1" />
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-auto inline-flex items-center justify-center h-9 w-9 rounded-full text-secondText bg-surface/80 backdrop-blur-sm hover:bg-surfaceAlt hover:text-text transition shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:ring-offset-surface"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}

// Scrollable body region. Most callers want a single Sheet.Body
// holding the form / content; the header + footer stay pinned.
// Padding mirrors the header's responsive scale so the three
// regions read as one column at every breakpoint.
Sheet.Body = function SheetBody({ children, className = "" }) {
  return (
    <div className={`flex-1 overflow-y-auto px-4 sm:px-5 py-4 ${className}`}>
      {children}
    </div>
  );
};

// Bottom action bar (Cancel / Save). Stays out of the scrolling
// region so primary actions are always reachable. Padding matches
// the header (px-4 sm:px-5, py-3 sm:py-3.5) — the slight bg-tint
// (surfaceAlt/40) is what visually separates the footer from the
// body without needing a hard border.
Sheet.Footer = function SheetFooter({ children, className = "" }) {
  return (
    <div
      className={`flex items-center justify-end gap-2 border-t border-border/60 bg-surfaceAlt/40 px-4 sm:px-5 py-3 sm:py-3.5 shrink-0 ${className}`}
    >
      {children}
    </div>
  );
};
